import { Button, Input, Tree, Typography } from "antd";
import classNames from "classnames";
import _, { isNull } from "lodash";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { LoadingIcon, SearchIcon } from "./Icons";
import { useGlobalFilterContext } from "contexts/GlobalFilterContext";
import {
  ARMORCODE_GROUP_NONE_VALUE,
  isFallingUnderGroup,
  parseGroupsToHierarchy,
} from "utils/armorcodeGroupUtil";
import ACMiddleEllipsis from "ac-components/ac-middle-ellipsis/ACMiddleEllipsis";

const { Text } = Typography;

export default function ArmorCodeGroupSelectionList({
  applyGlobalFilter = true,
  disabled,
  onChange,
  value,
  visible,
  allGroups,
  noneOption,
}) {
  const [noData, setNoData] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [maxListHeight, setMaxListHeight] = useState(360);
  const treeContainerRef = useRef();
  const visibleNodeRef = useRef({});
  const listContainerRef = useRef();
  const inputRef = useRef();
  const { globalFilterGroups } = useGlobalFilterContext();

  const calculateMaxHeight = () => {
    if(listContainerRef.current){
        const rect = listContainerRef.current.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const availableSpace = windowHeight - rect.top - 24; // 24px offset from bottom
        const bottomAndTopSpace = 120; // space for buttons and padding inside dropdown
        const maxHeight = Math.max(300, availableSpace - bottomAndTopSpace); // minimum 300px
        setMaxListHeight( maxHeight);
    }
  }

  useEffect(() => {
    let focusTimeOut;
    if(visible && inputRef.current){
      focusTimeOut = setTimeout(() => {
          inputRef.current.focus();
          calculateMaxHeight();
      }, 300);
    }
    return () => clearTimeout(focusTimeOut);
  }, [visible]);

  const onSearch = useCallback((e) => {
    setSearchText(e.target.value);
    requestAnimationFrame(() => {
      setNoData(treeContainerRef.current.offsetHeight === 0);
    });
  }, []);

  const groupHierarchy = useMemo(() => {
    if (_.isEmpty(allGroups)) {
      return [];
    }
    let groups = allGroups;
    if (applyGlobalFilter && !_.isEmpty(globalFilterGroups)) {
      groups = groups.filter((group) => {
        return globalFilterGroups.some((globalFilteredGroup) => {
          return isFallingUnderGroup(globalFilteredGroup, group);
        });
      });
    }
    groups = parseGroupsToHierarchy(groups || []).hierarchy;
    if (noneOption && _.isEmpty(globalFilterGroups)) {
      groups.unshift({
        title: `<None>`,
        key: ARMORCODE_GROUP_NONE_VALUE,
        children: [],
        groupValue: ["<None>"],
      });
    }
    return groups;
  }, [allGroups, globalFilterGroups, applyGlobalFilter, noneOption]);

  const onCheck = useCallback(
    (keys, { checkedNodes }) => {
      // selectedKeys contains all selected keys including all decendants/ancestors
      let selectedKeys = keys;
      checkedNodes.forEach((node) => {
        if (node.children.length > 0) {
          // if all the children nodes of a node are selected, remove them from selected array as parent node is present in selected
          const childrenKeys = node.children.map((child) => child.key);
          const childrenKeysSelected = _.intersection(
            selectedKeys,
            childrenKeys
          );
          if (childrenKeysSelected.length === childrenKeys.length) {
            selectedKeys = _.difference(selectedKeys, childrenKeysSelected);
          }
        }
      });

      if (_.isFunction(onChange)) {
        onChange(selectedKeys);
      }
    },
    [onChange]
  );

  useEffect(() => {
    if (!visible) {
      setSearchText("");
    }
  }, [visible]);

  const onSelectAll = useCallback(() => {
    if (_.isFunction(onChange) && !_.isEmpty(groupHierarchy)) {
      const selectedKeys = groupHierarchy
        .filter(({ groupValue }) => {
          return (
            visibleNodeRef.current[groupValue] ||
            visibleNodeRef.current[groupValue] === undefined
          );
        })
        .map(({ key }) => key);
      onChange(selectedKeys);
    }
  }, [onChange, groupHierarchy]);

  const onClearAll = useCallback(() => {
    if (_.isFunction(onChange)) {
      onChange([]);
    }
  }, [onChange]);

  return (
    <div
      ref={listContainerRef}
      className={classNames("armorcode-group-field-content-wrapper", {
        "search-active": !_.isEmpty(searchText),
      })}
    >
      <Input
        ref={inputRef}
        suffix={<SearchIcon />}
        className="m-b-sm"
        placeholder="Search"
        value={searchText}
        onChange={onSearch}
        allowClear
        disabled={disabled}
      />
      <div className="text-right">
        <Button
          className="p-l-xs"
          type="link"
          size="small"
          onClick={onSelectAll}
          disabled={disabled}
        >
          Select All
        </Button>
        <Button
          className="p-l-xs"
          type="link"
          size="small"
          onClick={onClearAll}
          disabled={disabled}
        >
          Clear All
        </Button>
      </div>
      <div ref={treeContainerRef} style={{ maxHeight: maxListHeight, overflow: "auto" }}>
        <Tree
          checkable
          disabled={disabled}
          selectable={false}
          checkedKeys={value}
          onCheck={onCheck}
          treeData={groupHierarchy}
          titleRender={(nodeData) => <ACMiddleEllipsis text={nodeData.title} />}
          filterTreeNode={(node) => {
            if (searchText) {
              const match = node.groupValue.includes(searchText);
              // If a node is selected and then a search is made for some other node and select all option is triggered
              // then this will retain the previous selection
              visibleNodeRef.current[node.groupValue] = match || node.checked;
              return match;
            } else {
              visibleNodeRef.current[node.groupValue] = undefined;
            }
            return false;
          }}
        />
      </div>
      {isNull(allGroups) ? (
        <div className="text-center p-y-m">
          <LoadingIcon />
        </div>
      ) : null}
      {noData ? (
        <div className="text-center">
          <Text type="secondary">No data</Text>
        </div>
      ) : null}
    </div>
  );
}
